import React, { Fragment } from 'react';
import { isEmpty, find, propEq, includes } from 'ramda';
import useApp from 'reducers/hooks/useApp';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@material-ui/core';
import Loader from 'sharedComponents/Loader';
import Seo from 'sharedComponents/Seo';

import { getDayOfYear } from 'utils/dateHelpers';

import useChats from 'reducers/hooks/useChats';
import useScreen from 'hooks/useScreen';
import useNotification from 'reducers/hooks/useNotification';
import useFirebaseChat from 'reducers/hooks/firebase/useChat';

import FirebaseChatPresenter from 'presenters/FirebaseChatPresenter';
import MessagePresenter from 'presenters/MessagePresenter';
import UserPresenter from 'presenters/UserPresenter';

import DesktopMessages from './components/DesktopMessages';
import MobileMessages from './components/MobileMessages';

const LoanOfficerMessages = () => {
  const { isDesktop } = useScreen();
  const { t } = useTranslation();

  const { sendMessageNotification } = useNotification();

  const {
    chats: firebaseChats,
    loadAndSubscribeToMessages,
    subscribedChatMessagesUids,
    isChatsLoading,
    sendMessage,
    readMessages,
    setChatFavorite,
    setChatHidden,
    changeSelectedChatUid,
    selectedChatUid,
    isOnline,
  } = useFirebaseChat();

  const { chats, updateChat, isUpdating } = useChats();

  const { currentUser } = useApp();

  const handleChatSelect = uid => () => {
    const chat = find(propEq('uid', uid), firebaseChats);
    const chatUid = FirebaseChatPresenter.uid(chat);
    changeSelectedChatUid(chatUid);

    const isSubscribedToChatMessages = includes(chatUid, subscribedChatMessagesUids);
    if (isSubscribedToChatMessages) return;
    loadAndSubscribeToMessages(chatUid);
  };

  const handleSendMessage = (participantId, chatUid, message) => {
    const dayOfYear = getDayOfYear();
    sendMessage(participantId, currentUser.id, chatUid, message, dayOfYear);
    const params = {
      recepientId: participantId,
      type: MessagePresenter.type(message),
      message: MessagePresenter.data(message),
    };

    sendMessageNotification({ notification: params });
  };

  const handleMessagesRead = chatUid => {
    readMessages(currentUser.id, chatUid);
  };

  const handleUnselectChat = () => {
    changeSelectedChatUid(null);
  };

  if (isChatsLoading) {
    return (
      <Box height="100%" display="flex" justifyContent="center" alignItems="center">
        <Loader />
      </Box>
    );
  }

  if (isEmpty(firebaseChats)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
        <Typography variant="h2">{t('components.LoanOfficerMessages.noChats')}</Typography>
      </Box>
    );
  }

  const handleSetChatAsFavorite = chatUid => () => {
    setChatFavorite(true, currentUser.id, chatUid);
  };
  const handleUnsetChatAsFavorite = chatUid => () => {
    setChatFavorite(false, currentUser.id, chatUid);
  };
  const handleSetChatHidden = chatUid => () => {
    setChatHidden(true, currentUser.id, chatUid);
  };
  const handleUnsetChatHidden = chatUid => () => {
    setChatHidden(false, currentUser.id, chatUid);
  };

  const handleToggleAutoFollowupsMessages = (chatUid, attributes) => {
    updateChat(chatUid, attributes);
  };

  const MesssagesComponent = isDesktop ? DesktopMessages : MobileMessages;

  return (
    <Fragment>
      <Seo title={t('meta.LoanOfficerMessages.title')} />
      <MesssagesComponent
        isOnline={isOnline}
        currentUser={currentUser}
        onChatSelect={handleChatSelect}
        onMessageSend={handleSendMessage}
        onMessagesRead={handleMessagesRead}
        selectedChatUid={selectedChatUid}
        chats={firebaseChats}
        chatsInfo={chats}
        hiddenChats={firebaseChats}
        favoriteChats={firebaseChats}
        chatsWithTasks={firebaseChats}
        onAddChatToFavorite={handleSetChatAsFavorite}
        onRemoveChatFromFavorite={handleUnsetChatAsFavorite}
        onAddChatToHidden={handleSetChatHidden}
        onRemoveChatFromHidden={handleUnsetChatHidden}
        onUnselectChat={handleUnselectChat}
        onToggleAutoFollowupsMessages={handleToggleAutoFollowupsMessages}
        isChatUpdating={isUpdating}
        isEnabledAutoFollowupsMessages={UserPresenter.enabledAutoFollowupsMessages(currentUser)}
      />
    </Fragment>
  );
};

export default LoanOfficerMessages;



// WEBPACK FOOTER //
// ./src/components/LoanOfficerMessages/LoanOfficerMessages.js